// Babylon scene: engine setup, lighting, placeholder car and the onboard camera.

import {
  Color3,
  Color4,
  DirectionalLight,
  Engine,
  FreeCamera,
  ImageProcessingConfiguration,
  HemisphericLight,
  Mesh,
  MeshBuilder,
  Scene,
  StandardMaterial,
  TransformNode,
  Vector3,
} from './babylon';
import { param } from '../config/params';
import { buildSky, HORIZON } from './sky';
import { blobShadowTexture } from './textures';
import { loadCarModel, spinWheels, type CarVisual } from './carModel';
import type { Material } from '@babylonjs/core/Materials/material';
import type { Car } from '../sim/car';
import { carParams } from '../sim/car';
import type { QualityProfile } from './quality';
import type { UltraEffects } from './ultra';

const DEG = Math.PI / 180;

export const renderParams = {
  maxDpr: param({ id: 'render.maxDpr', label: 'Max pixel ratio', group: 'Render', min: 0.5, max: 3, step: 0.25, value: 2 }),
  dynamicRes: param({ id: 'render.dynamicRes', label: 'Dynamic resolution (0/1)', group: 'Render', min: 0, max: 1, step: 1, value: 1 }),
  minScale: param({ id: 'render.minScale', label: 'Min resolution scale', group: 'Render', min: 0.3, max: 1, step: 0.05, value: 0.55 }),
  fovDeg: param({ id: 'cam.fovDeg', label: 'Vertical FOV (deg)', group: 'Camera', min: 30, max: 80, step: 1, value: 46 }),
  fovSpeedDeg: param({ id: 'cam.fovSpeedDeg', label: 'Extra FOV at speed (deg)', group: 'Camera', min: 0, max: 20, step: 0.5, value: 7 }),
  camHeight: param({ id: 'cam.height', label: 'T-cam height (m)', group: 'Camera', min: 0.6, max: 2, step: 0.01, value: 1.22 }),
  camForward: param({ id: 'cam.forward', label: 'T-cam forward (m)', group: 'Camera', min: -1.5, max: 1.5, step: 0.01, value: 0 }),
  camPitchDeg: param({ id: 'cam.pitchDeg', label: 'T-cam pitch down (deg)', group: 'Camera', min: -5, max: 15, step: 0.25, value: 3.5 }),
};

export type HorizonMode = 'world' | 'screen';

export interface Renderer {
  engine: Engine;
  /** Call once per frame: adjusts render resolution to hold the frame rate. */
  adaptResolution(frameDt: number): void;
  /** Current resolution scale (1 = full, up to the max pixel ratio). */
  readonly resolutionScale: number;
  /** True when dynamic resolution is already as low as it may go. */
  readonly atMinScale: boolean;
  /** Apply a graphics quality profile (pixel ratio, resolution floor, view distance, Ultra effects). */
  setQuality(q: QualityProfile): void;
  scene: Scene;
  camera: FreeCamera;
  /** `y` is the ground height under the car, `pitch` the road's nose-up angle (radians). */
  updateCar(car: Car, y: number, pitch: number): void;
  updateCamera(car: Car, phoneRoll: number, horizon: HorizonMode): void;
  /** Translucent ghost car; alpha 0 hides it. */
  updateGhost(pose: { x: number; y: number; z: number; heading: number } | undefined, alpha: number): void;
}

export function createRenderer(canvas: HTMLCanvasElement): Renderer {
  const engine = new Engine(
    canvas,
    true,
    { stencil: false, preserveDrawingBuffer: false, powerPreference: 'high-performance', disableWebGL2Support: false },
    false,
  );
  let resolutionScale = 1;
  let quality: QualityProfile = { maxDpr: 3, minScale: 1, trees: 1, viewDistance: 3200 };
  // On Ultra the profile's floor wins: below it, stepping down to High looks better.
  const minScale = (): number => (quality.ultra ? quality.minScale : Math.min(renderParams.minScale.value, quality.minScale));
  const applyScaling = (): void => {
    const dpr = Math.min(window.devicePixelRatio || 1, renderParams.maxDpr.value, quality.maxDpr);
    engine.setHardwareScalingLevel(1 / (dpr * resolutionScale));
  };
  // Dynamic resolution: drop render resolution when frames run long, raise it
  // again after a few seconds of headroom. Keeps older phones smooth.
  let windowTime = 0;
  let windowFrames = 0;
  let goodWindows = 0;
  let sinceChange = 0;
  const adaptResolution = (frameDt: number): void => {
    if (!renderParams.dynamicRes.value) {
      if (resolutionScale !== 1) {
        resolutionScale = 1;
        applyScaling();
      }
      return;
    }
    // Ultra: ignore the shader-compile stutter right after start-up or a change.
    sinceChange += frameDt;
    if (sinceChange < (quality.resolutionGrace ?? 0)) {
      windowTime = 0;
      windowFrames = 0;
      return;
    }
    windowTime += frameDt;
    windowFrames++;
    if (windowTime < 1) return;
    const fps = windowFrames / windowTime;
    windowTime = 0;
    windowFrames = 0;
    const min = minScale();
    if (fps < 50 && resolutionScale > min) {
      resolutionScale = Math.max(min, resolutionScale - 0.1);
      goodWindows = 0;
      applyScaling();
    } else if (fps > 58 && resolutionScale < 1) {
      if (++goodWindows >= 4) {
        resolutionScale = Math.min(1, resolutionScale + 0.05);
        goodWindows = 0;
        applyScaling();
      }
    } else {
      goodWindows = 0;
    }
  };
  applyScaling();
  window.addEventListener('resize', () => {
    applyScaling();
    engine.resize();
  });

  const scene = new Scene(engine);
  scene.clearColor = new Color4(HORIZON.r, HORIZON.g, HORIZON.b, 1);
  scene.ambientColor = new Color3(0.3, 0.3, 0.3);
  scene.fogMode = Scene.FOGMODE_LINEAR;
  // Tropical haze: distant scenery fades into the horizon colour of the sky.
  scene.fogStart = 250;
  scene.fogEnd = 2300;
  scene.fogColor = HORIZON.clone();
  scene.skipPointerMovePicking = true;

  // Film-style grading, applied inside the material shaders (no extra pass):
  // ACES tone mapping, a little contrast, and a gentle vignette.
  const ip = scene.imageProcessingConfiguration;
  ip.toneMappingEnabled = true;
  ip.toneMappingType = ImageProcessingConfiguration.TONEMAPPING_ACES;
  ip.exposure = 1.4;
  ip.contrast = 1.12;
  ip.vignetteEnabled = true;
  ip.vignetteWeight = 1.4;
  ip.vignetteStretch = 0.6;
  ip.vignetteColor = new Color4(0.05, 0.04, 0.03, 0);

  // Fixed late-afternoon sun: warm, low, from the west.
  const hemi = new HemisphericLight('sky', new Vector3(0, 1, 0), scene);
  hemi.intensity = 0.62;
  hemi.diffuse = new Color3(0.8, 0.88, 1.0);
  hemi.groundColor = new Color3(0.32, 0.34, 0.24);
  const sun = new DirectionalLight('sun', new Vector3(0.8, -0.45, 0.3).normalize(), scene);
  sun.intensity = 1.15;
  sun.diffuse = new Color3(1.0, 0.84, 0.64);
  sun.specular = new Color3(1.0, 0.85, 0.7);
  buildSky(scene, sun.direction.scale(-1));
  // Ultra effects load on demand (a separate chunk phones never fetch).
  let ultraFx: UltraEffects | undefined;
  let ultraLoading = false;
  const syncUltra = (): void => {
    if (quality.ultra && !ultraFx && !ultraLoading) {
      ultraLoading = true;
      void import('./ultra')
        .then((m) => {
          if (quality.ultra && !ultraFx) ultraFx = m.createUltraEffects(scene, camera, sun);
        })
        .catch(() => undefined)
        .finally(() => {
          ultraLoading = false;
        });
    } else if (!quality.ultra && ultraFx) {
      ultraFx.dispose();
      ultraFx = undefined;
    }
  };

  // Primitive placeholders show immediately; the real model replaces them once
  // loaded (and they remain if it can't be loaded).
  let carNode: CarNode = buildPlaceholderCar(scene, 'car');
  let carY = 0;
  let carPitch = 0;
  let ghost: CarNode = buildPlaceholderCar(scene, 'ghost', 0.35);
  ghost.root.setEnabled(false);
  void loadCarModel(scene, 'car-model')
    .then((visual) => {
      // Keep the placeholder's contact shadow on the real car.
      const shadow = carNode.root.getChildMeshes(true).find((m) => m.name === 'car-shadow');
      if (shadow) shadow.parent = visual.root;
      visual.root.position.copyFrom(carNode.root.position);
      visual.root.rotation.copyFrom(carNode.root.rotation);
      carNode.root.dispose();
      carNode = visual;
    })
    .catch(() => {
      // Model unavailable (offline before it was ever cached): keep the placeholder.
    });
  void loadCarModel(scene, 'ghost-model', { alpha: 0.35 })
    .then((visual) => {
      visual.root.setEnabled(ghost.root.isEnabled());
      ghost.root.dispose();
      ghost = visual;
    })
    .catch(() => undefined);

  const camera = new FreeCamera('tcam', new Vector3(0, 2, 0), scene);
  // Dev inspection camera: ?dev&cam=x,y,z,tx,ty,tz looks from a fixed point.
  const params = new URLSearchParams(location.search);
  const camArg = params.has('dev') ? params.get('cam')?.split(',').map(Number) : undefined;
  const inspect = camArg && camArg.length === 6 && camArg.every(Number.isFinite);
  if (inspect) {
    camera.position.set(camArg[0]!, camArg[1]!, camArg[2]!);
    camera.setTarget(new Vector3(camArg[3]!, camArg[4]!, camArg[5]!));
    camera.fov = 0.9;
  }
  // Tight near/far planes for depth precision: track layers are millimetres
  // apart and must not flicker at a distance. The haze hides everything past ~2.3 km.
  camera.minZ = 0.2;
  camera.maxZ = 3200;
  camera.inputs.clear();

  return {
    engine,
    scene,
    camera,
    adaptResolution,
    get resolutionScale() {
      return resolutionScale;
    },
    get atMinScale() {
      return !renderParams.dynamicRes.value || resolutionScale <= minScale() + 1e-6;
    },
    setQuality(q) {
      quality = q;
      sinceChange = 0;
      resolutionScale = Math.max(resolutionScale, minScale());
      applyScaling();
      syncUltra();
      // Haze closes in with the view distance so nothing pops at the far plane.
      camera.maxZ = q.viewDistance;
      scene.fogEnd = Math.min(2300, q.viewDistance * 0.75);
      scene.fogStart = 250 * (scene.fogEnd / 2300);
    },
    updateCar(car, y, pitch) {
      carY = y;
      carPitch = pitch;
      carNode.root.position.set(car.x, y, car.z);
      carNode.root.rotation.y = car.heading;
      // Babylon's positive x rotation pitches the nose down.
      carNode.root.rotation.x = -pitch;
      for (const w of carNode.frontWheels) w.rotation.y = car.wheelAngle;
      if (carNode.wheelRadii) spinWheels(carNode as CarVisual, engine.getDeltaTime() / 1000, car.speed);
    },
    updateGhost(pose, alpha) {
      const show = pose !== undefined && alpha > 0.01;
      ghost.root.setEnabled(show);
      if (!show) return;
      ghost.root.position.set(pose.x, pose.y, pose.z);
      ghost.root.rotation.y = pose.heading;
      for (const m of ghost.materials) m.alpha = alpha;
    },
    updateCamera(car, phoneRoll, horizon) {
      if (inspect) return;
      const s = Math.sin(car.heading);
      const c = Math.cos(car.heading);
      const fwd = renderParams.camForward.value;
      camera.position.set(car.x + s * fwd, carY + renderParams.camHeight.value, car.z + c * fwd);
      camera.rotation.x = renderParams.camPitchDeg.value * DEG - carPitch;
      camera.rotation.y = car.heading;
      // World-level horizon: roll the camera with the phone so the rendered
      // horizon stays level with the real world. Screen-locked: no roll.
      // (Babylon's positive camera roll turns the image clockwise on screen, so negate.)
      camera.rotation.z = horizon === 'world' ? -phoneRoll : 0;
      const speedFrac = Math.min(1, car.speed / 90);
      // The speed widening scales with the base FOV, so a narrow desktop view isn't stretched more.
      const fov = renderParams.fovDeg.value;
      camera.fov = (fov + renderParams.fovSpeedDeg.value * (fov / renderParams.fovDeg.defaultValue) * speedFrac * speedFrac) * DEG;
    },
  };
}

interface CarNode {
  root: TransformNode;
  frontWheels: TransformNode[];
  materials: Material[];
  /** Present on the loaded model (wheels can spin). */
  wheels?: TransformNode[];
  wheelRadii?: number[];
}

/** Rough open-wheel shape from primitives; only the nose, front wheels and halo matter from the T-cam. */
function buildPlaceholderCar(scene: Scene, name: string, alpha = 1): CarNode {
  const root = new TransformNode(name, scene);
  const L = carParams.wheelbase.value;
  const rearAxle = -L * carParams.frontWeight.value;
  const frontAxle = rearAxle + L;

  const ghostTint = alpha < 1;
  const body = mat(scene, `${name}-body`, ghostTint ? new Color3(0.75, 0.85, 1) : new Color3(0.08, 0.32, 0.55));
  const dark = mat(scene, `${name}-dark`, ghostTint ? new Color3(0.6, 0.7, 0.85) : new Color3(0.06, 0.06, 0.07));
  const accent = mat(scene, `${name}-accent`, new Color3(0.95, 0.75, 0.15));
  const materials = [body, dark, accent];
  for (const m of materials) {
    m.alpha = alpha;
    // The ghost glows a little so it stays visible against dark asphalt.
    if (ghostTint) m.emissiveColor = new Color3(0.35, 0.45, 0.6);
  }

  const parts: Mesh[] = [];
  const box = (name: string, w: number, h: number, d: number, x: number, y: number, z: number, m: StandardMaterial): Mesh => {
    const b = MeshBuilder.CreateBox(name, { width: w, height: h, depth: d }, scene);
    b.position.set(x, y, z);
    b.material = m;
    b.parent = root;
    parts.push(b);
    return b;
  };

  // Monocoque and nose taper towards the front wing.
  box('tub', 0.75, 0.55, 2.4, 0, 0.42, rearAxle + 1.9, body);
  box('nose1', 0.45, 0.32, 1.0, 0, 0.36, frontAxle - 0.2, body);
  box('nose2', 0.28, 0.2, 0.9, 0, 0.28, frontAxle + 0.65, body);
  box('frontWing', 1.75, 0.04, 0.45, 0, 0.12, frontAxle + 0.95, dark);
  box('frontFlap', 1.7, 0.03, 0.18, 0, 0.2, frontAxle + 0.8, accent);
  box('engine', 0.6, 0.6, 1.6, 0, 0.45, rearAxle + 0.5, body);
  box('airbox', 0.3, 0.35, 0.7, 0, 0.88, rearAxle + 1.1, body);
  box('rearWing', 1.0, 0.05, 0.35, 0, 0.95, rearAxle - 0.55, dark);
  box('sidepodL', 0.35, 0.4, 1.5, -0.55, 0.35, rearAxle + 1.3, body);
  box('sidepodR', 0.35, 0.4, 1.5, 0.55, 0.35, rearAxle + 1.3, body);
  // Halo: a hoop in front of the driver's head.
  const halo = MeshBuilder.CreateTorus('halo', { diameter: 0.85, thickness: 0.035, tessellation: 24 }, scene);
  halo.scaling.set(1, 1, 1.25);
  halo.position.set(0, 0.95, rearAxle + 1.85);
  halo.material = dark;
  halo.parent = root;
  box('haloPillar', 0.05, 0.3, 0.05, 0, 0.8, rearAxle + 2.38, dark);

  const frontWheels: TransformNode[] = [];
  const wheel = (x: number, z: number, radius: number, width: number, steer: boolean): void => {
    const hub = new TransformNode('hub', scene);
    hub.parent = root;
    hub.position.set(x, radius, z);
    const w = MeshBuilder.CreateCylinder('wheel', { diameter: radius * 2, height: width, tessellation: 20 }, scene);
    w.rotation.z = Math.PI / 2;
    w.material = dark;
    w.parent = hub;
    if (steer) frontWheels.push(hub);
  };
  // Tyre sizes per Pirelli 2026: 705/710 mm diameter, 280/375 mm wide.
  wheel(-0.85, frontAxle, 0.3525, 0.28, true);
  wheel(0.85, frontAxle, 0.3525, 0.28, true);
  wheel(-0.78, rearAxle, 0.355, 0.375, false);
  wheel(0.78, rearAxle, 0.355, 0.375, false);

  if (!ghostTint) {
    // Soft contact shadow; real shadow maps cost too much on phones for what the T-cam sees.
    const blob = MeshBuilder.CreateGround(`${name}-shadow`, { width: 2.3, height: 6.0 }, scene);
    blob.position.set(0, 0.035, (frontAxle + rearAxle) / 2 + 0.2);
    const blobMat = new StandardMaterial(`${name}-shadow-mat`, scene);
    blobMat.diffuseColor = Color3.Black();
    blobMat.specularColor = Color3.Black();
    blobMat.opacityTexture = blobShadowTexture(scene);
    blobMat.disableLighting = true;
    blob.material = blobMat;
    blob.parent = root;
    blob.isPickable = false;
  }
  return { root, frontWheels, materials };
}

function mat(scene: Scene, name: string, color: Color3): StandardMaterial {
  const m = new StandardMaterial(name, scene);
  m.diffuseColor = color;
  m.specularColor = new Color3(0.25, 0.25, 0.25);
  return m;
}
